import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from '../../SvgIcon';
import { CompIcon } from '../../CompIcon';
import { TeamIcon } from '../../TeamIcon';

const DROPDOWN_VERTICAL_OFFSET = 84;

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {Function} [props.itemToString] - Item to string.
 * @param {any} [props.initialSelectedItem] - Initial selected item.
 * @param {any} [props.activeItem] - Active item from state.
 * @param {Function} [props.onSelectedItemChange] - On change callback.
 * @param {boolean} [props.isDisabled] - Is select disabled.
 * @returns {React.Element} Component.
 */
export const SelectDesktop = (props) => {
    const selectRef = useRef();
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
        display: 'none'
    });

    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        getItemProps
    } = useSelect({
        items: props.items,
        initialSelectedItem: props.initialSelectedItem,
        selectedItem: props.activeItem,
        // @ts-ignore
        onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );

            if (typeof props.onSelectedItemChange === 'function') {
                props.onSelectedItemChange({
                    selectedItem: newSelectedItem
                });
            }
        }
    });

    const currentRound = props?.currentRoundIndex
        ? props.currentRoundIndex
        : false;

    const isDisabled = props?.isDisabled;

    /**
     * @param {any} item - Item.
     * @returns {string} Text.
     */
    const getItemText = (item) =>
        props.itemToString ? props.itemToString(item) : item;

    useLayoutEffect(() => {
        if (isOpen && selectRef.current) {
            const rect = selectRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY - DROPDOWN_VERTICAL_OFFSET,
                left: rect.left + window.scrollX,
                width: rect.width,
                display: 'block'
            });
        } else {
            setDropdownPosition((pos) => ({ ...pos, display: 'none' }));
        }
    }, [isOpen]);

    return (
        <div
            className={classNames(
                'select',
                {
                    'select--open': isOpen
                },
                {
                    'select--slim': props.displaySlim
                }
            )}
            ref={selectRef}
        >
            <button
                type="button"
                {...getToggleButtonProps({
                    id: `${PULSE.app.common.idifyString(
                        props.label
                    )}-dropdown-button`,
                    'aria-labelledby': !props.hideLabel
                        ? `${PULSE.app.common.idifyString(
                              props.label
                          )}-dropdown-label`
                        : false,
                    'aria-label': props.hideLabel ? props.label : false
                })}
                className="select__display"
                disabled={isDisabled}
            >
                {props.showTeamLogo && selectedItem.teamAbbr && (
                    <TeamIcon
                        className="select__option-logo-icon"
                        providerId={selectedItem.providerId}
                    />
                )}
                {props.showCompLogo && selectedItem?.code && (
                    <span
                        className={`competition-nav__comp-icon competition-nav__comp-icon--${selectedItem.code.toLowerCase()}`}
                    >
                        <CompIcon code={selectedItem.code} className="icon" />
                    </span>
                )}
                <span className="select__content">
                    {props.label && !props.hideLabel ? (
                        <label
                            {...getLabelProps({
                                id: `${PULSE.app.common.idifyString(
                                    props.label
                                )}-dropdown-label`
                            })}
                            className="select__label"
                        >
                            {props.label}
                        </label>
                    ) : null}
                    <span className="select__current">
                        <span className="select__current-text">
                            {getItemText(selectedItem) || ''}
                        </span>
                        <SvgIcon
                            className="select__chevron"
                            icon="chevron-down"
                        />
                    </span>
                </span>
            </button>
            {ReactDOM.createPortal(
                <div
                    style={{
                        position: 'absolute',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        zIndex: 80,
                        display: dropdownPosition.display
                    }}
                >
                    <div className="select__options-wrapper">
                        <ul
                            {...getMenuProps(
                                {
                                    'aria-labelledby': `${PULSE.app.common.idifyString(
                                        props.label
                                    )}-dropdown-label`
                                },
                                { suppressRefError: true }
                            )}
                            className={classNames('select__options-list', {
                                'u-hide': !isOpen
                            })}
                        >
                            {isOpen &&
                                props.items.map((item, index) => {
                                    const itemText = getItemText(item);
                                    const isSelected =
                                        getItemText(selectedItem) === itemText;

                                    return (
                                        <li
                                            key={`${itemText}${index}`}
                                            {...getItemProps({ item, index })}
                                            className={classNames(
                                                'select__option',
                                                {
                                                    'select__option--selected':
                                                        isSelected,
                                                    'select__option--current':
                                                        currentRound === index,
                                                    'select__option--highlighted':
                                                        highlightedIndex ===
                                                        index
                                                }
                                            )}
                                        >
                                            <div className="select__option-logo-name-wrapper">
                                                {props.showTeamLogo &&
                                                    item.teamAbbr && (
                                                        <TeamIcon
                                                            className="select__option-logo-icon"
                                                            providerId={
                                                                item.providerId
                                                            }
                                                        />
                                                    )}
                                                <span className="item-text">
                                                    {itemText}
                                                </span>
                                            </div>

                                            {isSelected &&
                                                !props.showTotals && (
                                                    <SvgIcon
                                                        icon="checkmark"
                                                        className="select__option-button-icon"
                                                    />
                                                )}

                                            {props.showTotals &&
                                                item.total !== null && (
                                                    <div className="select__option-total">
                                                        {item.total}
                                                    </div>
                                                )}
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

SelectDesktop.propTypes = {
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    initialSelectedItem: PropTypes.any,
    activeItem: PropTypes.any,
    onSelectedItemChange: PropTypes.func,
    currentRoundIndex: PropTypes.number,
    showCompLogo: PropTypes.bool,
    isDisabled: PropTypes.bool,
    hideLabel: PropTypes.bool,
    showTeamLogo: PropTypes.bool,
    displaySlim: PropTypes.bool,
    showTotals: PropTypes.bool
};
